/** VNNIC panel operations: the manual step staff do in VNNIC's web panel, and its actions. */

import { ClipboardCheck, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../api';
import type { PanelOperation } from '../../api/areas/namechange';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, Field } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, type DialogBodyProps } from '../operator/shared';
import { useEnum } from './shared';

export const PANEL_TONE: Record<string, Tone> = { PENDING: 'pending', SUBMITTED: 'info', COMPLETED: 'ok', REFUSED: 'crit', CANCELLED: 'neutral' };

export function PanelStatusBadge({ status }: { status: string }) {
  const e = useEnum();
  return <Badge tone={PANEL_TONE[status] ?? 'neutral'}>{e('panelStatus', status)}</Badge>;
}

/** What VNNIC must show: the new registrant contact, or the lock statuses set / cleared. */
export function PanelExpected({ op }: { op: PanelOperation }) {
  const { t } = useTranslation('services');
  if (op.expected.registrant) return <span className="font-mono text-xs">{t('panel.expectRegistrant', { handle: op.expected.registrant })}</span>;
  if (op.expected.statuses_present) return <span className="text-xs">{t('panel.expectSet', { statuses: op.expected.statuses_present.join(', ') })}</span>;
  if (op.expected.statuses_absent) return <span className="text-xs">{t('panel.expectCleared', { statuses: op.expected.statuses_absent.join(', ') })}</span>;
  return null;
}

function SubmittedForm({ close, changed, op }: DialogBodyProps & { op: PanelOperation }) {
  const { t } = useTranslation('services');
  const [reference, setReference] = useState(op.submitted_reference ?? '');
  const [note, setNote] = useState(op.submitted_note ?? '');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.recordPanelSubmitted(op.id, { reference: reference.trim() || undefined, note: note.trim() || undefined, reason: reason.trim() }, opts));
  const done = w.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('panel.submittedDone')}</Callout>
      ) : (
        <>
          <Field label={t('panel.reference')} hint={t('panel.referenceHint')}>
            <Input value={reference} onChange={(ev) => setReference(ev.target.value)} maxLength={200} />
          </Field>
          <Field label={t('panel.note')}>
            <Textarea value={note} onChange={(ev) => setNote(ev.target.value)} rows={2} maxLength={2000} />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={done ? undefined : t('panel.recordSubmitted')} onClose={close} />
    </form>
  );
}

/** "Submitted to VNNIC", "Check at VNNIC now" and — lock / unlock only — "VNNIC refused". */
export function PanelOperationActions({ op, onChanged }: { op: PanelOperation; onChanged: () => void }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const check = useWrite((_: null, opts) => api.checkPanelOperation(op.id, opts));
  if (op.status !== 'PENDING' && op.status !== 'SUBMITTED') return null;
  const outcome = check.state.phase === 'done' ? check.state.result.outcome : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <ActionDialog label={t('panel.recordSubmitted')} icon={ClipboardCheck} title={`${t('panel.recordSubmittedTitle')} · ${e('panelOperation', op.operation)}`} onChanged={onChanged}>
        {(p) => <SubmittedForm {...p} op={op} />}
      </ActionDialog>
      <Button type="button" size="sm" variant="outline" disabled={check.busy} onClick={() => void check.run(null).then((r) => r?.outcome === 'Completed' && onChanged())}>
        <RefreshCw /> {t('panel.checkNow')}
      </Button>
      {op.operation !== 'NAME_CHANGE' ? (
        <ReasonAction
          label={t('panel.refused')}
          icon={X}
          danger
          variant="ghost"
          title={`${t('panel.refusedTitle')} · ${e('panelOperation', op.operation)}`}
          warning={t('panel.refusedWarning')}
          run={(reason, opts) => api.recordPanelRefusal(op.id, { reason }, opts)}
          done={() => t('panel.refusedDone')}
          onChanged={onChanged}
        />
      ) : null}
      {outcome && outcome !== 'Completed' ? <span className="text-xs text-muted-foreground">{t('panel.notYet')}</span> : null}
      {check.state.phase === 'error' ? <WriteError error={check.state.error} /> : null}
    </span>
  );
}
